import {mappedFields} from '@/lib/StandardFields';
import {InfoWindowContent} from '../InfoWindowContent';
import {DisplayField} from '../DisplayField';
import type {VectorTileGraphic} from '@/services/GraphicService/VectorTileGraphic/VectorTileGraphic';
import type {MVTLayerPickingInfo} from '@deck.gl/geo-layers';

/** Returns a title to be used within InfoWindowContent. */
function assignTitle(feature: {layerName: string; method?: string}): string {
  switch (feature.layerName) {
    case 'power_switch': {
      return 'Switch';
    }
    case 'power_tower': {
      return 'Power Tower';
    }
    case 'power_line': {
      return 'Power Line';
    }
    case 'power_transformer': {
      return 'Transformer';
    }
    case 'power_compensator': {
      return 'Compensator';
    }
    case 'power_substation_point': {
      return 'Substation';
    }
    case 'power_substation': {
      return 'Substation';
    }
    case 'power_generator_area': {
      return 'Solar panel';
    }
    case 'power_generator': {
      switch (feature.method) {
        case 'wind_turbine': {
          return 'Wind turbine';
        }
      }
      return 'Solar panel';
    }
    case 'petroleum_pipeline': {
      return 'Pipeline';
    }
    case 'power_plant': {
      return 'Power Plant';
    }
    case 'petroleum_site': {
      return 'Petroleum Site';
    }
    case 'petroleum_well': {
      return 'Petroleum Well';
    }
    case 'water_reservoir': {
      return 'Water Reservoir';
    }
    default: {
      return feature.layerName;
    }
  }
}

/** Given a feature from an OSM layer, returns InfoWindowContent data for it. */
function createOsmContentData(hostLayerName: string, feature: MVTLayerPickingInfo['object']['properties'], graphic: VectorTileGraphic) {
  try {
    const id: string = feature['osm_id'];
    const title: string = feature['name'] ?? assignTitle(feature as {layerName: string; method?: string});
    const displayFields: DisplayField[] = [];

    mappedFields[hostLayerName].forEach((field) => {
      let value = feature[field['openStreetMapKey']];

      if (field.unit === 'date' && value) {
        value = new Date(value).toLocaleDateString();
      }

      // If the value is empty, set it to undefined to prevent the Display Field from showing
      if (!value) {
        value = undefined;
      }

      // Convert string numbers to numbers
      if (typeof value === 'string' && !isNaN(parseFloat(value)) && field.unit !== 'date') {
        value = parseFloat(value).toFixed(2);
      }

      displayFields.push(new DisplayField({
        label: field.label,
        unit: field.unit,
        allowEdit: false,
        allowLabelEdit: false,
        allowDelete: false,
        value: value,
      }));
    });

    return {
      type: 'vectorTile',
      title,
      id,
      metadata: {},
      host: {setHighlight: (state: boolean) => graphic.setHighlight(state, feature)},
      displayFields,
    };
  } catch (error) {
    error.message = 'Error creating tile layer content data: ' + error.message;
    console.warn(error);
    return null;
  }
}

/** Given a feature from an OSM layer, returns an InfoWindowContent object for it. */
function createOsmContent(hostLayerName: string, feature: MVTLayerPickingInfo['object']['properties'], graphic: VectorTileGraphic) {
  const contentData = createOsmContentData(hostLayerName, feature, graphic);
  if (!contentData) return null;
  return new InfoWindowContent(contentData);
}

export {
  createOsmContent,
};
